import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, Eye, Clock, TrendingDown, Globe, Smartphone, Monitor, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import { Button } from '../ui/button';

const AdminAnalytics = () => {
  const { apiCall } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState('7');
  const [visitorsPage, setVisitorsPage] = useState(1);
  const [visitors, setVisitors] = useState(null);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const result = await apiCall(`/admin/analytics/summary?days=${days}`);
      setData(result);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitors = async (page = 1) => {
    try {
      const result = await apiCall(`/admin/analytics/visitors?page=${page}&limit=20`);
      setVisitors(result);
      setVisitorsPage(page);
    } catch (err) {
      console.error('Failed to fetch visitors:', err);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchVisitors(1);
  }, [days]);

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString('ro-RO', { 
        day: '2-digit', 
        month: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return isoString;
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'google': return '🔍';
      case 'facebook': return '📘';
      case 'instagram': return '📷';
      case 'twitter': return '🐦';
      case 'referral': return '🔗';
      default: return '🌐';
    }
  };

  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <Monitor className="h-4 w-4" />;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone className="h-4 w-4" />;
    }
    return <Monitor className="h-4 w-4" />;
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1625]">Analytics</h1>
          <p className="text-muted-foreground">Website visitor statistics and insights</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Time period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24 hours</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Visitors</p>
                <h3 className="text-3xl font-bold text-[#1A1625]">{data?.summary?.total_visitors || 0}</h3>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Page Views</p>
                <h3 className="text-3xl font-bold text-[#1A1625]">{data?.summary?.total_page_views || 0}</h3>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <Eye className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bounce Rate</p>
                <h3 className="text-3xl font-bold text-[#1A1625]">{data?.summary?.bounce_rate || 0}%</h3>
              </div>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <TrendingDown className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Duration</p>
                <h3 className="text-3xl font-bold text-[#1A1625]">{formatDuration(data?.summary?.avg_session_duration || 0)}</h3>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Traffic Sources */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Traffic Sources
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.sources && Object.entries(data.sources).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(data.sources)
                  .sort((a, b) => b[1] - a[1])
                  .map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{getSourceIcon(source)}</span>
                        <span className="capitalize">{source}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-32 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-primary h-2 rounded-full" 
                            style={{ width: `${(count / data.summary.total_visitors * 100)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium w-10 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No traffic data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Top Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data?.top_pages && data.top_pages.length > 0 ? (
              <div className="space-y-3">
                {data.top_pages.map((page, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm truncate max-w-[200px]" title={page.page}>
                      {page.page === '/' ? 'Homepage' : page.page}
                    </span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${(page.views / data.top_pages[0].views * 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-10 text-right">{page.views}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-center py-8">No page view data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Visitors Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Visitors</CardTitle>
        </CardHeader>
        <CardContent>
          {visitors?.visitors && visitors.visitors.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-2">Time</th>
                      <th className="text-left py-3 px-2">IP Address</th>
                      <th className="text-left py-3 px-2">Source</th>
                      <th className="text-left py-3 px-2">Device</th>
                      <th className="text-left py-3 px-2">Pages</th>
                      <th className="text-left py-3 px-2">Bounce</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visitors.visitors.map((visitor, index) => (
                      <tr key={index} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-2">{formatDate(visitor.started_at)}</td>
                        <td className="py-3 px-2 font-mono text-xs">{visitor.ip_address}</td>
                        <td className="py-3 px-2">
                          <span className="flex items-center gap-1">
                            {getSourceIcon(visitor.source)}
                            <span className="capitalize">{visitor.source}</span>
                          </span>
                        </td>
                        <td className="py-3 px-2">{getDeviceIcon(visitor.user_agent)}</td>
                        <td className="py-3 px-2">{visitor.pages?.length || 0}</td>
                        <td className="py-3 px-2">
                          {visitor.is_bounce ? (
                            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded">Yes</span>
                          ) : (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-muted-foreground">
                  Page {visitorsPage} of {visitors.pages} ({visitors.total} total)
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fetchVisitors(visitorsPage - 1)}
                    disabled={visitorsPage <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => fetchVisitors(visitorsPage + 1)}
                    disabled={visitorsPage >= visitors.pages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground text-center py-8">No visitors tracked yet. Visit your store to start collecting data!</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
